import { Instance, SnapshotIn, types, flow, getEnv, getSnapshot } from 'mobx-state-tree';

import { IStoresEnv } from '@core/storesEnv';
import { BaseItem } from '@shared/stores/BaseItem.store';
import { getOptionsByNameId, convertStringsToOptions } from '@shared/helpers/form';
import {
    performGetUserDepartments,
    performGetUserAssignableRoles,
    performGetAllFilters,
    performGetUsersWithDetails,
} from '../services';

const UserItemWithDetails = types.model('UserItemWithDetails', {
    id: types.string,
    name: types.string,
    email: types.maybeNull(types.string),
    groupIds: types.optional(types.array(types.string), []),
});

export const FiltersOptionsStoreInferred = types
    .model('FiltersOptionsStoreInferred',
        {
            departments: types.array(types.string),
            roles: types.array(BaseItem),
            assignableRoles: types.array(BaseItem),
            statuses: types.array(BaseItem),
            groups: types.array(BaseItem),
            procedures: types.array(BaseItem),
            users: types.array(UserItemWithDetails),
        })
    .views(self => ({
        get getFilterOptions() {
            return getSnapshot(self);
        },
        get getDepartmentsOptions() {
            return convertStringsToOptions(self.departments);
        },
        get getRolesOptions() {
            return getOptionsByNameId(self.roles);
        },
        get getAssignableRolesOptions() {
            return getOptionsByNameId(self.assignableRoles);
        },
        get getStatusesOptions() {
            return getOptionsByNameId(self.statuses);
        },
        get getGroupsOptions() {
            return getOptionsByNameId(self.groups);
        },
        get getProceduresOptions() {
            return getOptionsByNameId(self.procedures);
        },
        get getUsersOptions() {
            return self.users.map(u => ({ value: u.id, label: u.name }));
        },
        get getUsersOptionsWithDetails() {
            return self.users.map(u => ({
                value: u.id,
                label: u.name,
                email: u.email ?? '',
                groupIds: u.groupIds.slice(),
            }));
        },
    }))
    .actions(self => {
        const { api } = getEnv<IStoresEnv>(self);

        const loadFilters = flow(function* () {
            const result = yield performGetAllFilters(
                api,
                {
                    payload: {},
                    errorHandlers: { DEFAULT: '' },
                },
            );
            if (result.data) {
                // self.departments = result.data.departments; // Wrong format from BE here
                self.roles = result.data.roles;
                self.statuses = result.data.status;
                self.groups = result.data.groups;
                self.procedures = result.data.procedures;
            }
        });

        const loadDepartments = flow(function* () {
            const result = yield performGetUserDepartments(
                api,
                {
                    payload: {},
                    errorHandlers: { DEFAULT: '' },
                },
            );
            if (result.data) {
                self.departments = result.data;
            }
        });

        const loadAssignableRoles = flow(function* () {
            const result = yield performGetUserAssignableRoles(
                api,
                {
                    payload: {},
                    errorHandlers: { DEFAULT: '' },
                },
            );
            if (result.data) {
                self.assignableRoles = result.data;
            }
        });

        const loadUsers = flow(function* () {
            // Use the richer Users/Search endpoint to get email + group memberships
            const result = yield performGetUsersWithDetails(
                api,
                {
                    payload: {},
                    errorHandlers: { DEFAULT: '' },
                },
            );
            if (result.data) {
                self.users = result.data.map((u: any) => ({
                    id: u.id,
                    name: u.name,
                    email: u.email ?? null,
                    groupIds: u.groupIds ?? [],
                }));
            }
        });

        const loadFilterOptions = flow(function* () {
            yield Promise.all([
                loadFilters(),
                loadDepartments(),
                loadAssignableRoles(),
                loadUsers(),
            ])
        });

        return {
            loadFilterOptions,
            loadUsers,
        }
    })

type FiltersOptionsStoreFactoryType = typeof FiltersOptionsStoreInferred;
interface IFiltersOptionsStoreFactory extends FiltersOptionsStoreFactoryType { }
export const FiltersOptionsStore: IFiltersOptionsStoreFactory = FiltersOptionsStoreInferred;
export interface IFiltersOptionsStore extends Instance<IFiltersOptionsStoreFactory> { }
export interface IFiltersOptionsStoreSnapshotIn extends SnapshotIn<IFiltersOptionsStore> { }
